"use client";
import { useState } from "react"
import { motion } from "framer-motion"
import { useNavigate } from 'react-router-dom';
import { Search, UserPlus, Users, X, Check } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Separator } from "@/components/ui/separator"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/hooks/use-toast"

// Sample data - in a real app, this would come from an API
const sampleFriends = [
  {
    id: "2",
    name: "Jamie Wilson",
    email: "jamie@example.com",
    avatar: "/placeholder.svg?height=40&width=40",
    role: "UX Designer",
    company: "DesignHub",
  },
  {
    id: "4",
    name: "Casey Johnson",
    email: "casey@example.com",
    avatar: "/placeholder.svg?height=40&width=40",
    role: "Marketing Director",
    company: "BrandWorks",
  },
  {
    id: "5",
    name: "Riley Smith",
    email: "riley@example.com",
    avatar: "/placeholder.svg?height=40&width=40",
    role: "Software Engineer",
    company: "CodeCraft",
  },
]

const sampleContacts = [
  {
    id: "3",
    name: "Taylor Reed",
    email: "taylor@example.com",
    avatar: "/placeholder.svg?height=40&width=40",
    role: "Engineering Director",
    company: "InnovateTech",
  },
  {
    id: "6",
    name: "Jordan Lee",
    email: "jordan@example.com",
    avatar: "/placeholder.svg?height=40&width=40",
    role: "Venture Capitalist",
    company: "Future Fund",
  },
  {
    id: "7",
    name: "Morgan Chen",
    email: "morgan@example.com",
    avatar: "/placeholder.svg?height=40&width=40",
    role: "CTO",
    company: "TechGrowth",
  },
]

const CreateConnection = () => {
  const [selectedFriend, setSelectedFriend] = useState(null)
  const [selectedContact, setSelectedContact] = useState(null)
  const [message, setMessage] = useState("")
  const [searchFriend, setSearchFriend] = useState("")
  const [searchContact, setSearchContact] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { toast } = useToast()
  const navigate = useNavigate();

  const filteredFriends = sampleFriends.filter((friend) =>
    friend.name.toLowerCase().includes(searchFriend.toLowerCase()) ||
    friend.company.toLowerCase().includes(searchFriend.toLowerCase()) ||
    friend.role.toLowerCase().includes(searchFriend.toLowerCase()))

  const filteredContacts = sampleContacts.filter((contact) =>
    contact.name.toLowerCase().includes(searchContact.toLowerCase()) ||
    contact.company.toLowerCase().includes(searchContact.toLowerCase()) ||
    contact.role.toLowerCase().includes(searchContact.toLowerCase()))

  const handleSubmit = () => {
    if (!selectedFriend || !selectedContact) {
      toast({
        title: "Missing information",
        description: "Please select both a friend and a contact.",
        variant: "destructive",
      })
      return
    }

    setIsSubmitting(true)

    // In a real app, this would be an API call
    setTimeout(() => {
      // Store the connection data in localStorage for demo purposes
      // In a real app, this would be handled by your state management or backend
      localStorage.setItem("referralData", JSON.stringify({
        friend: selectedFriend,
        contact: selectedContact,
        message: message,
        timestamp: new Date().toISOString(),
      }))

      setIsSubmitting(false)

      // Navigate to the success page
      navigate("/dashboard/referral-success")
    }, 1500)
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-green-50 to-white p-4 md:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-3xl mx-auto">
        <Card className="shadow-lg border-green-100">
          <CardHeader className="bg-green-50 rounded-t-lg">
            <CardTitle className="text-2xl text-green-800 flex items-center gap-2">
              <UserPlus className="h-6 w-6" />
              Create Professional Connection
            </CardTitle>
            <CardDescription className="text-green-700">
              Introduce your friend to a professional contact
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-6">
            <Tabs defaultValue="select" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="select">Select People</TabsTrigger>
                <TabsTrigger value="message">Craft Message</TabsTrigger>
              </TabsList>

              <TabsContent value="select" className="space-y-6">
                {/* Friend Selection */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <Label className="text-lg font-medium text-green-800">Select a Friend</Label>
                    {selectedFriend && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedFriend(null)}
                        className="h-8 text-red-500 hover:text-red-700">
                        <X className="h-4 w-4 mr-1" /> Clear
                      </Button>
                    )}
                  </div>

                  <div className="relative">
                    <Search
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search friends..."
                      value={searchFriend}
                      onChange={(e) => setSearchFriend(e.target.value)}
                      className="pl-10 border-green-200" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {filteredFriends.map((friend) => (
                      <motion.div
                        key={friend.id}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setSelectedFriend(friend)}
                        className={`flex items-center p-3 rounded-lg cursor-pointer border ${
                          selectedFriend?.id === friend.id
                            ? "border-green-500 bg-green-50"
                            : "border-gray-200 hover:border-green-200"
                        }`}>
                        <Avatar className="h-10 w-10 mr-3">
                          <AvatarImage src={friend.avatar} alt={friend.name} />
                          <AvatarFallback className="bg-green-100 text-green-800">
                            {friend.name
                              .split(" ")
                              .map((n) => n[0])
                              .join("")}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{friend.name}</p>
                          <p className="text-sm text-gray-500 truncate">
                            {friend.role} at {friend.company}
                          </p>
                        </div>
                        {selectedFriend?.id === friend.id && (
                          <div className="ml-2 flex-shrink-0 text-green-500">
                            <Check className="h-5 w-5" />
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </div>

                <Separator className="bg-green-100" />

                {/* Contact Selection */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <Label className="text-lg font-medium text-green-800">Select a Contact</Label>
                    {selectedContact && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedContact(null)}
                        className="h-8 text-red-500 hover:text-red-700">
                        <X className="h-4 w-4 mr-1" /> Clear
                      </Button>
                    )}
                  </div>

                  <div className="relative">
                    <Search
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search contacts..."
                      value={searchContact}
                      onChange={(e) => setSearchContact(e.target.value)}
                      className="pl-10 border-green-200" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {filteredContacts.map((contact) => (
                      <motion.div
                        key={contact.id}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setSelectedContact(contact)}
                        className={`flex items-center p-3 rounded-lg cursor-pointer border ${
                          selectedContact?.id === contact.id
                            ? "border-green-500 bg-green-50"
                            : "border-gray-200 hover:border-green-200"
                        }`}>
                        <Avatar className="h-10 w-10 mr-3">
                          <AvatarImage src={contact.avatar} alt={contact.name} />
                          <AvatarFallback className="bg-green-100 text-green-800">
                            {contact.name
                              .split(" ")
                              .map((n) => n[0])
                              .join("")}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{contact.name}</p>
                          <p className="text-sm text-gray-500 truncate">
                            {contact.role} at {contact.company}
                          </p>
                        </div>
                        {selectedContact?.id === contact.id && (
                          <div className="ml-2 flex-shrink-0 text-green-500">
                            <Check className="h-5 w-5" />
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="message" className="space-y-6">
                {/* Introduction Preview */}
                {(selectedFriend || selectedContact) && (
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h3 className="font-medium text-green-800 mb-2">Introduction Preview</h3>
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span>You are introducing</span>
                      {selectedFriend ? (
                        <span
                          className="font-medium bg-white px-2 py-1 rounded-md border border-green-200">
                          {selectedFriend.name}
                        </span>
                      ) : (
                        <span className="italic text-gray-500">(Select a friend)</span>
                      )}
                      <span>to</span>
                      {selectedContact ? (
                        <span
                          className="font-medium bg-white px-2 py-1 rounded-md border border-green-200">
                          {selectedContact.name}
                        </span>
                      ) : (
                        <span className="italic text-gray-500">(Select a contact)</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Message Input */}
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-lg font-medium text-green-800">
                    Introduction Message
                  </Label>
                  <Textarea
                    id="message"
                    placeholder="Write a brief introduction message..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="min-h-[150px] border-green-200" />
                  <p className="text-sm text-gray-500">
                    This message will be shared with both parties when you create the connection.
                  </p>
                </div>

                {/* Message Template */}
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <h3 className="font-medium text-gray-800 mb-2 flex items-center gap-1">
                    <Users className="h-4 w-4" /> Message Template
                  </h3>
                  <p className="text-sm text-gray-600 mb-2">Not sure what to write? Use this template:</p>
                  <div className="text-sm bg-white p-3 rounded border border-gray-200">
                    <p>Hi [Contact's Name],</p>
                    <p className="my-2">
                      I'd like to introduce you to [Friend's Name], who is a [Friend's Role] at [Friend's Company]. I
                      thought you two should connect because of your shared interest in [common interest/industry].
                    </p>
                    <p>
                      [Friend's Name], [Contact's Name] is a [Contact's Role] at [Contact's Company] and has extensive
                      experience in [area of expertise].
                    </p>
                    <p className="mt-2">I'll let you two take it from here!</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => {
                      if (selectedFriend && selectedContact) {
                        setMessage(`Hi ${selectedContact.name},

I'd like to introduce you to ${selectedFriend.name}, who is a ${selectedFriend.role} at ${selectedFriend.company}. I thought you two should connect because of your shared professional interests.

${selectedFriend.name}, ${selectedContact.name} is a ${selectedContact.role} at ${selectedContact.company} and has extensive experience in their field.

I'll let you two take it from here!`)
                      } else {
                        toast({
                          title: "Missing information",
                          description: "Please select both a friend and a contact first.",
                          variant: "destructive",
                        })
                      }
                    }}>
                    Use Template
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>

          <CardFooter className="bg-green-50 rounded-b-lg flex justify-between">
            <Button variant="outline" className="border-green-500 text-green-700">
              Save Draft
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!selectedFriend || !selectedContact || isSubmitting}
              className="bg-green-600 hover:bg-green-700 text-white">
              {isSubmitting ? "Creating..." : "Create Connection"}
            </Button>
          </CardFooter>
        </Card>
      </motion.div>
    </div>
  );
}

export default CreateConnection
